/**
 * /memorial/:id — PUBLIC read-only page for a memorial that has been published.
 * No auth required. Anyone with the link can view + read the guestbook.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import MultilingualText from "@/components/ui/MultilingualText";
import StoryCard from "@/components/app/StoryCard";
import { mlPrimary } from "@/lib/ml";
import { fileUrl } from "@/lib/files";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicMemorial() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/v1/memorials/public/${id}`);
        setData(data);
      } catch (e) {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(e?.response?.data?.detail || "Something went wrong");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center text-[color:var(--ink-soft)]">
        Opening the memorial…
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center">
        <div className="overline mb-3">Not public</div>
        <h1 className="heritage-title text-3xl">This memorial is kept private.</h1>
        <p className="mt-3 font-italic-serif italic text-[color:var(--ink-soft)]">
          Not every family opens their archive to the world — and that is a
          kind of remembering, too.
        </p>
        <Link to="/" className="btn-ghost mt-8 inline-flex">Return home</Link>
      </div>
    );
  }

  if (!data) return null;

  const { person, memorial } = data;
  const stories = data.stories || [];
  const linkedDisasters = data.disasters || [];
  const fullName = [person.first_name, person.middle_name, person.last_name].filter(Boolean).join(" ");
  const years = [person.birth_date, person.death_date].filter(Boolean).join(" – ");
  return (
    <section className="mx-auto max-w-4xl px-6 md:px-10 pt-10 pb-24">
      <motion.header
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="text-center"
      >
        <div className="overline">In loving memory of</div>
        <h1
          data-testid="public-memorial-name"
          className="heritage-title mt-2 leading-[1.02] tracking-[-0.01em]"
          style={{ fontSize: "clamp(36px, 5.4vw, 72px)" }}
        >
          {fullName || "Unnamed"}
        </h1>
        {years && (
          <div className="mt-3 font-italic-serif italic text-lg text-[color:var(--sepia)]">
            {years}
          </div>
        )}
        {(person.birth_place || person.death_place) && (
          <div className="mt-1 numeric-index">
            {person.death_place || person.birth_place}
          </div>
        )}
      </motion.header>

      {/* Connected disaster record */}
      {linkedDisasters.length > 0 && (
        <div
          className="mt-8 border border-[color:var(--sepia)]/30 bg-[color:var(--parchment-2)]/50 px-6 py-4 text-center"
          data-testid="public-memorial-disasters"
        >
          <div className="overline text-[color:var(--sepia)] mb-1">Remembered in the record of</div>
          {linkedDisasters.map((d) => (
            <Link
              key={d.id}
              to={`/disasters/${d.id}`}
              data-testid={`public-memorial-disaster-${d.id}`}
              className="link-underline font-serif-display text-lg text-[color:var(--ink)] hover:text-[color:var(--rust)]"
            >
              {d.name}{d.country ? ` · ${d.country}` : ""}
            </Link>
          ))}
        </div>
      )}

      {(person.photo_url || person.bio) && (
        <div className="mt-10 grid md:grid-cols-2 gap-8 items-start">
          {person.photo_url && (
            <div className="vintage-frame max-w-xs mx-auto">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={fileUrl(person.photo_url)} alt={fullName} />
              </div>
            </div>
          )}
          {person.bio && (
            <MultilingualText
              value={person.bio}
              className="font-italic-serif italic text-lg leading-[1.65] text-[color:var(--ink-soft)] max-w-[52ch]"
              testid="public-memorial-bio"
            />
          )}
        </div>
      )}

      {/* Life stories */}
      {stories.length > 0 && (
        <section className="mt-16" data-testid="public-stories">
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
            <h2 className="heritage-title text-2xl md:text-3xl">Life stories</h2>
            <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          </div>
          <div className="space-y-8">
            {stories.map((s) => (
              <StoryCard key={s.id} story={s} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-14">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          <h2 className="heritage-title text-2xl md:text-3xl inline-flex items-center gap-2">
            <Flame size={20} className="text-[color:var(--rust)]" />
            Candles lit · <span className="tabular-nums">{(memorial.candles || []).length}</span>
          </h2>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-8 gap-3 mb-8">
          {(memorial.candles || []).map((c, i) => (
            <div key={i} className="text-center" title={mlPrimary(c.note) || c.name}>
              <Flame
                size={26}
                className="mx-auto text-[color:var(--rust)] drop-shadow-[0_0_10px_rgba(156,66,33,0.55)]"
              />
              <div className="mt-1 text-[10px] text-[color:var(--ink-soft)] truncate">
                {c.name.split(" ")[0]}
              </div>
              {c.note && (
                <MultilingualText
                  value={c.note}
                  quoted
                  className="mt-1 text-[11px] leading-snug font-italic-serif italic text-[color:var(--sepia)] line-clamp-3"
                />
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mt-6">
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
          <h2 className="heritage-title text-2xl md:text-3xl">Family guestbook</h2>
          <div className="flex-1 h-px bg-[color:var(--sepia)]/30" />
        </div>
        <div className="space-y-6">
          {(memorial.messages || []).length === 0 && (
            <p className="text-center font-italic-serif italic text-[color:var(--ink-soft)]">
              The family have not left messages yet.
            </p>
          )}
          {(memorial.messages || []).map((m, i) => (
            <article
              key={i}
              className="border-l-2 border-[color:var(--sepia)]/60 pl-4 py-1"
            >
              <div className="overline text-[color:var(--sepia)]">
                {m.name} ·{" "}
                {new Date(m.created_at).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </div>
              <MultilingualText
                value={m.message}
                quoted
                className="mt-1 font-italic-serif italic text-lg leading-snug"
              />
            </article>
          ))}
        </div>
      </section>

      <div className="mt-12 text-center text-sm">
        <p className="text-[color:var(--ink-soft)] mb-3">
          Family members can light candles and add messages after signing in.
        </p>
        <Link to="/app/login" className="btn-wood">Sign in to contribute</Link>
      </div>

      <div className="mt-16">
        <div className="flourish w-64 mx-auto" />
      </div>
    </section>
  );
}
